import React from "react";
import { useNavigate } from "react-router-dom";
import {
  Card,
  Table,
  Tag,
  Space,
  Typography,
  Button,
  Row,
  Col,
  Statistic,
  Badge,
  Spin,
  Avatar,
} from "antd";
import {
  DollarOutlined,
  BookOutlined,
  UserOutlined,
  CheckCircleOutlined,
  CloseCircleOutlined,
  ClockCircleOutlined,
} from "@ant-design/icons";

import { useAdminPaymentLedger } from "../Hooks/usePaymentLedger";
import { PaymentStatusEnum, PaymentStatusLabels, PaymentStatusColors } from "Common/Constants/Enums/PaymentStatusEnum";
import { PaymentSourceTypeColors, PaymentSourceTypeLabels } from "Common/Constants/Enums/PaymentSourceTypeEnum";
import dayjs from "dayjs";

const { Title, Text } = Typography;

const AdminPaymentLedgerPage = () => {
  const navigate = useNavigate();
  const { payments, loading, handlePageChange, refresh } = useAdminPaymentLedger();

  // Calculate statistics
  const totalRevenue = payments.items.reduce((sum, payment) => sum + payment.amount, 0);
  const totalTransactions = payments.totalCount;
  const successfulPayments = payments.items.filter(p => p.status === PaymentStatusEnum.Paid).length;
  const pendingPayments = payments.items.filter(p => p.status === PaymentStatusEnum.Pending).length;
  const failedPayments = payments.items.filter(p => p.status === PaymentStatusEnum.Failed).length;
  const averageTransaction = totalTransactions > 0 ? totalRevenue / totalTransactions : 0;

  // Format currency
  const formatCurrency = (amount, currency = "USD") => {
    return new Intl.NumberFormat('en-US', { style: 'currency', currency, minimumFractionDigits: 2 }).format(amount);
  };

  // Format date
  const formatDate = (dateString) => dayjs(dateString).format("DD MMM YYYY, HH:mm");

  // Get status icon
  const getStatusIcon = (status) => {
    switch (status) {
      case PaymentStatusEnum.Pending:
        return <ClockCircleOutlined style={{ color: '#faad14' }} />;
      case PaymentStatusEnum.Paid:
        return <CheckCircleOutlined style={{ color: '#52c41a' }} />;
      case PaymentStatusEnum.Canceled:
      case PaymentStatusEnum.Failed:
        return <CloseCircleOutlined style={{ color: '#ff4d4f' }} />;
      default:
        return null;
    }
  };

  // Table columns with responsive rules
  const columns = [
    {
      title: "Learner Code",
      dataIndex: ["learner", "learnerCode"],
      key: "learnerCode",
      width: 100,
      render: (learnerCode) => <Text strong>{learnerCode || "N/A"}</Text>,
      responsive: ['sm', 'md', 'lg', 'xl'],
    },
    {
      title: "Learner",
      dataIndex: "learner",
      key: "learner",
      render: (learner) => (
        <Space>
          <Avatar size="small" src={learner?.profileImage} icon={<UserOutlined />} />
          <div>
            <Text strong style={{ fontSize: '14px', display: 'block' }}>
              {learner?.firstName} {learner?.lastName}
            </Text>
            <Text type="secondary" style={{ fontSize: '12px' }}>
              {learner?.email}
            </Text>
          </div>
        </Space>
      ),
    },
    {
      title: "Amount",
      dataIndex: "amount",
      key: "amount",
      width: 120,
      align: 'right',
      render: (amount, record) => <Text strong style={{ fontSize: '14px' }}>{formatCurrency(amount, record.currency)}</Text>,
      responsive: ['sm', 'md', 'lg', 'xl'],
    },
    {
      title: "Source",
      dataIndex: "sourceType",
      key: "sourceType",
      width: 130,
      render: (sourceType) => <Tag color={PaymentSourceTypeColors[sourceType]}>{PaymentSourceTypeLabels[sourceType]}</Tag>,
      responsive: ['md', 'lg', 'xl'],
    },
    {
      title: "Status",
      dataIndex: "status",
      key: "status",
      width: 120,
      render: (status) => (
        <Space>
          {getStatusIcon(status)}
          <Badge status={PaymentStatusColors[status]} text={PaymentStatusLabels[status]} />
        </Space>
      ),
      responsive: ['sm', 'md', 'lg', 'xl'],
    },
    {
      title: "Provider",
      dataIndex: "paymentProvider",
      key: "paymentProvider",
      width: 100,
      render: (provider) => <Tag color="blue">{provider || "-"}</Tag>,
      responsive: ['lg', 'xl'],
    },
    {
      title: "Paid Date",
      dataIndex: "paidAt",
      key: "paidAt",
      width: 160,
      render: (paidAt) => <Text style={{ fontSize: '13px' }}>{formatDate(paidAt)}</Text>,
      responsive: ['md', 'lg', 'xl'],
    },
    {
      title: "Actions",
      key: "actions",
      width: 80,
      render: (_, record) => (
        <Button
          type="link"
          size="small"
          onClick={() => navigate(`/proAdmin-dashboard/payment-ledger-detail/${record.id}`)}
        >
          View
        </Button>
      ),
      responsive: ['xs', 'sm', 'md', 'lg', 'xl'],
    }
  ];

  return (
    <div style={{ padding: '20px' }}>
      {/* Header */}
      <div style={{ marginBottom: 24 }}>
        <Title level={3} style={{ marginBottom: 8 }}>
          <DollarOutlined style={{ marginRight: 12, color: '#1890ff' }} />
          Payment Management
        </Title>
        <Text type="secondary">
          Admin view - Manage all payment transactions across the platform
        </Text>
      </div>

      {/* Statistics */}
      <Row gutter={[16, 16]} style={{ marginBottom: 24 }}>
        {[{
          title: 'Total Revenue', value: totalRevenue, color: '#3f8600', prefix: '$', precision: 2
        },{
          title: 'Total Transactions', value: totalTransactions
        },{
          title: 'Successful', value: successfulPayments, color: '#52c41a'
        },{
          title: 'Pending', value: pendingPayments, color: '#faad14'
        },{
          title: 'Failed', value: failedPayments, color: '#ff4d4f'
        },{
          title: 'Avg. Transaction', value: averageTransaction, color: '#1890ff', prefix: '$', precision: 2
        }].map((stat, idx) => (
          <Col xs={24} sm={12} md={4} key={idx}>
            <Card size="small">
              <Statistic
                title={stat.title}
                value={stat.value}
                precision={stat.precision}
                prefix={stat.prefix}
                valueStyle={{ color: stat.color || '#000', fontSize: '20px' }}
              />
            </Card>
          </Col>
        ))}
      </Row>

      {/* Payments Table */}
      <Card
        title={
          <Space>
            <BookOutlined />
            <Text strong>All Payment Transactions</Text>
            <Tag color="blue">{payments.totalCount}</Tag>
          </Space>
        }
      >
        <Spin spinning={loading}>
          {payments.items.length === 0 && !loading ? (
            <div style={{ textAlign: 'center', padding: '60px 20px' }}>
              <DollarOutlined style={{ fontSize: '64px', color: '#d9d9d9', marginBottom: 16 }} />
              <Title level={4} style={{ color: '#bfbfbf', marginBottom: 8 }}>No Transactions Found</Title>
              <Text type="secondary">No payment transactions available for the selected filters</Text>
              <div style={{ marginTop: 16 }}>
                <Button type="primary" onClick={refresh}>Refresh</Button>
              </div>
            </div>
          ) : (
            <Table
              columns={columns}
              dataSource={payments.items}
              rowKey="id"
              pagination={{
                current: payments.pageNumber,
                pageSize: payments.pageSize,
                total: payments.totalCount,
                showSizeChanger: true,
                pageSizeOptions: ['10', '20', '50', '100'], // <--- added options
                showQuickJumper: true,
                showTotal: (total, range) => `Showing ${range[0]}-${range[1]} of ${total} transactions`,
                onChange: handlePageChange
              }}
              size="middle"
              scroll={{ x: 'max-content' }}
              style={{ overflowX: 'auto' }}
            />
          )}
        </Spin>
      </Card>
    </div>
  );
};

export default AdminPaymentLedgerPage;
